import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import * as api from '../../services/candidatePoolsApi';
import type {
  ImportResult,
  PersonReviewStatus,
  PoolMember,
  PoolMemberTopSkill,
  PoolUser,
  PoolWithRole,
  SheetLink,
} from '../../services/candidatePoolsApi';
import { ImportApiError } from '../../services/candidatePoolsApi';
import {
  INITIAL_IMPORT_STATE,
  classifyImportError,
  importReducer,
  type ImportMode,
  type ImportSuccessSummary,
} from './importState';
import { ImportProgressPanel } from './ImportProgressPanel';
import { AddPoolMemberModal } from './AddPoolMemberModal';

type ImportTab = 'paste' | 'cv' | 'bulk' | 'sheets';

/** Timing used for optimistic step advancement in the 4-step rail. */
const UPLOAD_TO_PARSING_MS = 1500;
const PARSING_TO_SAVING_MS = 11_000;
const SUCCESS_TOAST_DISMISS_MS = 8_000;
const ROW_HIGHLIGHT_MS = 5_000;

function buildSuccessSummary(result: ImportResult): ImportSuccessSummary {
  const p = result.persist;
  const applied = p?.applied ?? {};
  return {
    person_id: result.person_id,
    name: applied.name ?? null,
    title: applied.title ?? null,
    primary_location: applied.primary_location ?? null,
    seniority_level: applied.seniority_level ?? null,
    years_experience_total: applied.years_experience_total ?? null,
    experience_rows: p?.experience_rows ?? 0,
    project_rows: p?.project_rows ?? 0,
    skill_rows: p?.skill_rows ?? 0,
    skill_phrase_rows: p?.skill_phrase_rows ?? 0,
    language_rows: p?.language_rows ?? 0,
    review_status: p?.review_status ?? 'unreviewed',
    parser_version: p?.parser_version ?? 'unknown',
  };
}

interface CandidatePoolsPageProps {
  initialPoolId: number | null;
  onNavigatePool: (poolId: number | null) => void;
  onBack: () => void;
  currentUserEmail: string;
  onNavigatePerson?: (personId: number) => void;
  /**
   * Fired once per successful CV/paste import so the parent can refresh any
   * global people caches (e.g. App's `allPeople`). Not called on error or
   * user-initiated abort. Errors thrown by the callback are swallowed here so
   * a noisy refresh never masks import success.
   */
  onPeopleChanged?: () => void | Promise<void>;
  /**
   * Phase 3F — start review-queue mode in the profile modal. The parent owns
   * the modal + queue state: this callback just tells it which ids to walk
   * through and which one to land on first. When omitted, Review CTAs fall
   * back to `onNavigatePerson`.
   */
  onStartReviewQueue?: (firstPersonId: number, queue: number[]) => void;
  /**
   * Phase 3F — imperative handle setter so the parent can trigger a pool
   * refresh after a successful review-status change in the modal. We expose
   * this via a callback so consumers never need to reach into refs.
   */
  onRegisterPoolRefresh?: (refresh: () => void) => void;
  /**
   * When true, page is rendered under the global app header (flex column);
   * use height constraints and scroll the body instead of full-viewport layout.
   */
  embedded?: boolean;
}

export const CandidatePoolsPage: React.FC<CandidatePoolsPageProps> = ({
  initialPoolId,
  onNavigatePool,
  onBack,
  currentUserEmail,
  onNavigatePerson,
  onPeopleChanged,
  onStartReviewQueue,
  onRegisterPoolRefresh,
  embedded = false,
}) => {
  const [pools, setPools] = useState<PoolWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(initialPoolId);
  const [members, setMembers] = useState<PoolMember[]>([]);
  const [poolUsers, setPoolUsers] = useState<PoolUser[]>([]);
  const [sheetLinks, setSheetLinks] = useState<SheetLink[]>([]);
  const [role, setRole] = useState<string>('viewer');
  const [importTab, setImportTab] = useState<ImportTab>('paste');
  const [pasteText, setPasteText] = useState('');
  const [newPoolName, setNewPoolName] = useState('');
  const [poolNameEditing, setPoolNameEditing] = useState(false);
  const [poolNameDraft, setPoolNameDraft] = useState('');
  const [savingPoolName, setSavingPoolName] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareRole, setShareRole] = useState('editor');
  const [sheetIdInput, setSheetIdInput] = useState('');
  const [sheetRangeInput, setSheetRangeInput] = useState('Sheet1!A:Z');

  // --------------------------------------------------------------------------
  // Phase 3F — review-queue filter pill
  // --------------------------------------------------------------------------
  // `reviewFilter` lives only while the pool page is mounted; switching pools
  // resets it to 'all' because needs_review counts are per-pool.
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needs_review'>('all');
  useEffect(() => {
    setReviewFilter('all');
  }, [selectedId]);

  useEffect(() => {
    setPoolNameEditing(false);
    setPoolNameDraft('');
  }, [selectedId]);

  const needsReviewCount = members.reduce(
    (n, m) => (m.person_review_status === 'needs_review' ? n + 1 : n),
    0
  );
  const displayedMembers =
    reviewFilter === 'needs_review'
      ? members.filter((m) => m.person_review_status === 'needs_review')
      : members;
  const needsReviewIds = members
    .filter((m) => m.person_review_status === 'needs_review')
    .map((m) => m.person_id);

  const handleStartQueue = useCallback(() => {
    if (!onStartReviewQueue || needsReviewIds.length === 0) return;
    onStartReviewQueue(needsReviewIds[0], needsReviewIds);
  }, [onStartReviewQueue, needsReviewIds]);

  const handleRowReview = useCallback(
    (personId: number) => {
      if (onStartReviewQueue && needsReviewIds.includes(personId)) {
        // Rotate the queue so this candidate is first but the full backlog is
        // still reachable via Next.
        const pivot = needsReviewIds.indexOf(personId);
        const rotated = [...needsReviewIds.slice(pivot), ...needsReviewIds.slice(0, pivot)];
        onStartReviewQueue(personId, rotated);
      } else if (onNavigatePerson) {
        onNavigatePerson(personId);
      }
    },
    [onStartReviewQueue, onNavigatePerson, needsReviewIds]
  );

  // Expose a refresh handle to the parent so modal-driven review actions can
  // re-fetch the members list (updated review_status + counter).
  const selectedIdRef = useRef(selectedId);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  const loadPoolDetailRef = useRef<((poolId: number) => void) | null>(null);
  useEffect(() => {
    if (!onRegisterPoolRefresh) return;
    onRegisterPoolRefresh(() => {
      const pid = selectedIdRef.current;
      const fn = loadPoolDetailRef.current;
      if (pid != null && fn) fn(pid);
    });
  }, [onRegisterPoolRefresh]);

  const loadPools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listPools();
      setPools(list);
    } catch (e: any) {
      setError(e.message || 'Failed to load pools');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPoolDetail: (poolId: number) => Promise<void> = useCallback(async (poolId: number) => {
    setError(null);
    try {
      const [detail, mems, users, links] = await Promise.all([
        api.getPool(poolId),
        api.listMembers(poolId),
        api.listPoolUsers(poolId),
        api.listSheetLinks(poolId),
      ]);
      setRole(detail.role);
      setMembers(mems);
      setPoolUsers(users);
      setSheetLinks(links);
    } catch (e: any) {
      setError(e.message || 'Failed to load pool');
      setSelectedId(null);
      onNavigatePool(null);
    }
  }, [onNavigatePool]);

  useEffect(() => {
    loadPoolDetailRef.current = loadPoolDetail;
  }, [loadPoolDetail]);

  useEffect(() => {
    loadPools();
  }, [loadPools]);

  useEffect(() => {
    setSelectedId(initialPoolId);
  }, [initialPoolId]);

  useEffect(() => {
    if (selectedId != null) {
      loadPoolDetail(selectedId);
    } else {
      setMembers([]);
      setPoolUsers([]);
      setSheetLinks([]);
    }
  }, [selectedId, loadPoolDetail]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sheets') === 'connected') {
      setError(null);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('sheets_error')) {
      setError(`Sheets: ${params.get('sheets_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const canWrite = role === 'owner' || role === 'editor';
  const canAdmin = role === 'owner';
  const selectedPool = selectedId != null ? pools.find((p) => p.id === selectedId) ?? null : null;
  const canRenamePool = canWrite && !selectedPool?.is_internal_directory;
  const canManualAdd = canWrite && !selectedPool?.is_internal_directory;

  const handleManualAddSuccess = useCallback(
    async (result: api.ManualPoolMemberResult) => {
      setShowAddPersonModal(false);
      setManualAddNotice(
        result.updated_existing
          ? 'Updated existing candidate in this pool.'
          : 'Person added to pool.'
      );
      setHighlightPersonId(result.person_id);
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightPersonId((prev) => (prev === result.person_id ? null : prev));
      }, ROW_HIGHLIGHT_MS);
      if (selectedId != null) {
        await loadPoolDetail(selectedId);
      }
      if (onPeopleChanged) {
        try {
          await onPeopleChanged();
        } catch (refreshErr) {
          console.warn('onPeopleChanged failed after manual add:', refreshErr);
        }
      }
    },
    [selectedId, loadPoolDetail, onPeopleChanged]
  );

  const handleStartPoolRename = () => {
    if (!selectedPool || !canRenamePool) return;
    setPoolNameDraft(selectedPool.name);
    setPoolNameEditing(true);
  };

  const handleCancelPoolRename = () => {
    setPoolNameEditing(false);
    setPoolNameDraft('');
  };

  const handleSavePoolRename = async () => {
    if (selectedId == null || !selectedPool) return;
    const trimmed = poolNameDraft.trim();
    if (!trimmed) {
      setError('Pool name is required');
      return;
    }
    if (trimmed === selectedPool.name) {
      handleCancelPoolRename();
      return;
    }
    setSavingPoolName(true);
    try {
      await api.updatePool(selectedId, { name: trimmed });
      setPools((prev) => prev.map((p) => (p.id === selectedId ? { ...p, name: trimmed } : p)));
      handleCancelPoolRename();
    } catch (e: any) {
      setError(e.message || 'Failed to rename pool');
    } finally {
      setSavingPoolName(false);
    }
  };

  const handleCreatePool = async () => {
    if (!newPoolName.trim()) return;
    try {
      const id = await api.createPool(newPoolName.trim());
      setNewPoolName('');
      await loadPools();
      setSelectedId(id);
      onNavigatePool(id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSelectPool = (id: number) => {
    setSelectedId(id);
    onNavigatePool(id);
  };

  const [importState, dispatchImport] = useReducer(importReducer, INITIAL_IMPORT_STATE);
  const [highlightPersonId, setHighlightPersonId] = useState<number | null>(null);
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [manualAddNotice, setManualAddNotice] = useState<string | null>(null);
  const importAbortRef = useRef<AbortController | null>(null);
  const lastFileRef = useRef<File | null>(null);
  const lastPasteRef = useRef<string | null>(null);
  const importStepTimersRef = useRef<number[]>([]);
  const elapsedTimerRef = useRef<number | null>(null);
  const successAutoDismissRef = useRef<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);

  const clearStepTimers = useCallback(() => {
    importStepTimersRef.current.forEach((t) => window.clearTimeout(t));
    importStepTimersRef.current = [];
    if (elapsedTimerRef.current != null) {
      window.clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearStepTimers();
      if (successAutoDismissRef.current != null) {
        window.clearTimeout(successAutoDismissRef.current);
      }
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
      }
      importAbortRef.current?.abort();
    };
  }, [clearStepTimers]);

  const runImport = useCallback(
    async (mode: ImportMode) => {
      if (!selectedId) return;
      if (mode === 'cv' && !lastFileRef.current) return;
      if (mode === 'paste' && !lastPasteRef.current) return;

      clearStepTimers();
      if (successAutoDismissRef.current != null) {
        window.clearTimeout(successAutoDismissRef.current);
        successAutoDismissRef.current = null;
      }
      importAbortRef.current?.abort();
      const controller = new AbortController();
      importAbortRef.current = controller;

      const startedAt = performance.now();
      dispatchImport({
        type: 'start',
        mode,
        fileName: mode === 'cv' ? lastFileRef.current?.name ?? null : null,
        startedAt,
      });

      elapsedTimerRef.current = window.setInterval(() => {
        dispatchImport({ type: 'tick', now: performance.now() });
      }, 500);

      importStepTimersRef.current.push(
        window.setTimeout(() => {
          dispatchImport({ type: 'advance_to_parsing' });
        }, UPLOAD_TO_PARSING_MS)
      );
      importStepTimersRef.current.push(
        window.setTimeout(() => {
          dispatchImport({ type: 'advance_to_saving' });
        }, UPLOAD_TO_PARSING_MS + PARSING_TO_SAVING_MS)
      );

      try {
        const result =
          mode === 'cv'
            ? await api.importCv(selectedId, lastFileRef.current as File, {
                signal: controller.signal,
              })
            : await api.importPaste(selectedId, lastPasteRef.current as string, {
                signal: controller.signal,
              });
        clearStepTimers();
        const summary = buildSuccessSummary(result);
        dispatchImport({ type: 'success', summary });
        if (mode === 'paste') setPasteText('');
        setHighlightPersonId(summary.person_id);
        if (highlightTimerRef.current != null) {
          window.clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = window.setTimeout(() => {
          setHighlightPersonId((prev) => (prev === summary.person_id ? null : prev));
        }, ROW_HIGHLIGHT_MS);
        await loadPoolDetail(selectedId);
        // Fire ONCE per successful import. Swallow errors so a broken parent
        // refresh never masks the user-visible success state.
        if (onPeopleChanged) {
          try {
            await onPeopleChanged();
          } catch (refreshErr) {
            console.warn('onPeopleChanged failed after import:', refreshErr);
          }
        }
        successAutoDismissRef.current = window.setTimeout(() => {
          dispatchImport({ type: 'reset' });
          successAutoDismissRef.current = null;
        }, SUCCESS_TOAST_DISMISS_MS);
      } catch (err) {
        clearStepTimers();
        if (err instanceof DOMException && err.name === 'AbortError') {
          dispatchImport({ type: 'cancel' });
          return;
        }
        const httpStatus =
          err instanceof ImportApiError && err.status != null ? err.status : null;
        dispatchImport({ type: 'fail', error: classifyImportError(err, httpStatus) });
      }
    },
    [selectedId, clearStepTimers, loadPoolDetail, onPeopleChanged]
  );

  const handleImportPaste = async () => {
    if (!selectedId || !pasteText.trim()) return;
    lastPasteRef.current = pasteText;
    lastFileRef.current = null;
    await runImport('paste');
  };

  const handleCvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedId) return;
    lastFileRef.current = file;
    lastPasteRef.current = null;
    await runImport('cv');
  };

  const handleImportCancel = () => {
    importAbortRef.current?.abort();
  };

  const handleImportDismiss = () => {
    if (successAutoDismissRef.current != null) {
      window.clearTimeout(successAutoDismissRef.current);
      successAutoDismissRef.current = null;
    }
    dispatchImport({ type: 'reset' });
  };

  const handleImportRetry = async () => {
    if (lastFileRef.current) {
      await runImport('cv');
    } else if (lastPasteRef.current) {
      await runImport('paste');
    }
  };

  const handleImportOpenPerson = (personId: number) => {
    onNavigatePerson?.(personId);
  };

  const isImporting =
    importState.phase === 'uploading' ||
    importState.phase === 'parsing' ||
    importState.phase === 'saving';

  const handleBulkChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedId) return;
    try {
      const r = await api.importBulk(selectedId, file);
      await loadPoolDetail(selectedId);
      if (r.errors?.length) {
        setError(r.errors.slice(0, 3).join('; '));
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleConnectSheets = async () => {
    if (!selectedId || !sheetIdInput.trim()) return;
    try {
      const url = await api.getSheetsOAuthUrl(selectedId, sheetIdInput.trim(), sheetRangeInput || null);
      window.location.href = url;
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSync = async (linkId: number) => {
    if (!selectedId) return;
    try {
      const r = await api.syncSheet(selectedId, linkId);
      await loadPoolDetail(selectedId);
      if (r.errors?.length) {
        setError(r.errors.join('; '));
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleShareAdd = async () => {
    if (!selectedId || !shareEmail.trim()) return;
    try {
      await api.addPoolUser(selectedId, shareEmail.trim().toLowerCase(), shareRole);
      setShareEmail('');
      await loadPoolDetail(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleUpdateUserRole = async (email: string, newRole: string) => {
    if (!selectedId) return;
    try {
      await api.updatePoolUserRole(selectedId, email, newRole);
      await loadPoolDetail(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleRemoveUser = async (email: string) => {
    if (!selectedId) return;
    try {
      await api.removePoolUser(selectedId, email);
      await loadPoolDetail(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleRemoveMember = async (personId: number) => {
    if (!selectedId) return;
    const msg =
      members.length <= 1
        ? 'Remove this person from the pool? If they have no other recruiting pools, they will be archived and hidden from search.'
        : 'Remove this person from this pool?';
    if (!window.confirm(msg)) return;
    try {
      await api.removeMember(selectedId, personId);
      await loadPoolDetail(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeletePool = async () => {
    if (!selectedId || !canAdmin) return;
    if (!window.confirm('Delete this pool and all memberships?')) return;
    try {
      await api.deletePool(selectedId);
      setSelectedId(null);
      onNavigatePool(null);
      await loadPools();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeleteLink = async (linkId: number) => {
    if (!selectedId) return;
    try {
      await api.deleteSheetLink(selectedId, linkId);
      await loadPoolDetail(selectedId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading && pools.length === 0) {
    return (
      <div
        className={`bg-dark-bg flex items-center justify-center text-dark-text-secondary ${
          embedded ? 'flex-1 min-h-0 py-12' : 'min-h-screen'
        }`}
      >
        Loading pools…
      </div>
    );
  }

  return (
    <div
      className={`bg-dark-bg text-dark-text ${
        embedded ? 'flex flex-1 flex-col min-h-0 h-full' : 'min-h-screen'
      }`}
    >
      {!embedded && (
        <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-dark-bg to-gray-800 -z-10" />
      )}
      <header
        className={`border-b border-dark-border bg-dark-card px-4 py-3 flex items-center gap-4 flex-wrap shrink-0 ${
          embedded ? 'z-10' : 'sticky top-0 z-10'
        }`}
      >
        <button
          type="button"
          onClick={onBack}
          className="px-3 py-1.5 bg-brand-blue hover:bg-blue-700 text-white rounded-lg text-sm"
        >
          Back to main
        </button>
        <h1 className="text-xl font-bold flex-1">Candidate pools</h1>
      </header>

      <div
        className={`max-w-6xl mx-auto p-4 grid grid-cols-1 md:grid-cols-12 gap-4 ${
          embedded ? 'flex-1 min-h-0 overflow-y-auto' : ''
        }`}
      >
        {error && (
          <div className="md:col-span-12 rounded-lg bg-red-900/30 border border-red-800 text-red-200 px-3 py-2 text-sm">
            {error}
            <button type="button" className="ml-2 underline" onClick={() => setError(null)}>
              Dismiss
            </button>
          </div>
        )}

        <aside className="md:col-span-4 space-y-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3">
            <h2 className="text-sm font-semibold text-dark-text-secondary mb-2">Your pools</h2>
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {pools.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectPool(p.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedId === p.id
                        ? 'bg-indigo-600/30 border border-indigo-500 text-white'
                        : 'hover:bg-gray-800 border border-transparent'
                    }`}
                  >
                    <div className="font-medium flex items-center gap-2">
                      <span>{p.name}</span>
                      {p.is_internal_directory && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-800 border border-slate-600 text-slate-300">
                          System
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-dark-text-secondary capitalize">{p.role}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3">
            <h2 className="text-sm font-semibold text-dark-text-secondary mb-2">New pool</h2>
            <input
              className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm mb-2"
              placeholder="Pool name"
              value={newPoolName}
              onChange={(e) => setNewPoolName(e.target.value)}
            />
            <button
              type="button"
              onClick={handleCreatePool}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-sm text-white"
            >
              Create pool
            </button>
          </div>
        </aside>

        <main className="md:col-span-8 space-y-4">
          {selectedId == null && (
            <div className="bg-dark-card border border-dark-border rounded-xl p-8 text-center text-dark-text-secondary">
              Select a pool or create one to import candidates.
            </div>
          )}

          {selectedId != null && (
            <>
              <div className="bg-dark-card border border-dark-border rounded-xl p-4 flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  {poolNameEditing ? (
                    <div className="space-y-2 max-w-md">
                      <input
                        type="text"
                        data-testid="pool-rename-input"
                        className="w-full bg-dark-bg border border-indigo-500 rounded-lg px-3 py-2 text-lg font-bold text-dark-text"
                        value={poolNameDraft}
                        onChange={(e) => setPoolNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleSavePoolRename();
                          }
                          if (e.key === 'Escape') handleCancelPoolRename();
                        }}
                        disabled={savingPoolName}
                        autoFocus
                        aria-label="Pool name"
                      />
                      <p className="text-xs text-dark-text-secondary">
                        Enter to save. Escape to cancel.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void handleSavePoolRename()}
                          disabled={savingPoolName}
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm text-white disabled:opacity-50"
                        >
                          {savingPoolName ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelPoolRename}
                          disabled={savingPoolName}
                          className="px-3 py-1.5 rounded-lg border border-dark-border text-sm text-dark-text-secondary hover:bg-gray-800 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2
                          data-testid="pool-header-name"
                          className="text-lg font-bold truncate"
                        >
                          {selectedPool?.name || 'Pool'}
                        </h2>
                        {selectedPool?.is_internal_directory && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-200 border border-blue-800">
                            System pool
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-dark-text-secondary mt-1 capitalize">
                        Your role: {role}
                      </p>
                      {selectedPool?.is_internal_directory && (
                        <p className="text-xs text-gray-500 mt-0.5">Name cannot be changed</p>
                      )}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {canRenamePool && !poolNameEditing && (
                    <button
                      type="button"
                      data-testid="pool-rename-button"
                      onClick={handleStartPoolRename}
                      className="px-3 py-1.5 rounded-lg border border-indigo-500/60 text-sm text-indigo-200 hover:bg-indigo-900/30"
                    >
                      Rename
                    </button>
                  )}
                  {canAdmin && (
                    <button
                      type="button"
                      onClick={handleDeletePool}
                      className="px-3 py-1.5 rounded-lg bg-red-900/40 hover:bg-red-900/60 text-sm text-red-200"
                    >
                      Delete pool
                    </button>
                  )}
                </div>
              </div>

              {(canAdmin || poolUsers.length > 0) && (
                <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-cyan-400 mb-3">Pool access</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-dark-text-secondary border-b border-dark-border">
                          <th className="pb-2 pr-2">Email</th>
                          <th className="pb-2 pr-2 w-40">Role</th>
                          {canAdmin && <th className="pb-2 w-14 text-center"> </th>}
                        </tr>
                      </thead>
                      <tbody>
                        {poolUsers.map((u) => (
                          <tr key={u.id} className="border-b border-dark-border/50">
                            <td className="py-2 pr-2 align-middle">
                              <span className="break-all">{u.user_email}</span>
                              {u.user_email.toLowerCase() === currentUserEmail.toLowerCase() && (
                                <span className="text-xs text-gray-500 ml-1">(you)</span>
                              )}
                            </td>
                            <td className="py-2 pr-2 align-middle">
                              {canAdmin ? (
                                <select
                                  className="w-full max-w-[11rem] bg-dark-bg border border-dark-border rounded-lg px-2 py-1.5 text-sm capitalize"
                                  value={u.role}
                                  onChange={(e) => handleUpdateUserRole(u.user_email, e.target.value)}
                                >
                                  <option value="owner">Owner</option>
                                  <option value="editor">Editor</option>
                                  <option value="viewer">Viewer</option>
                                </select>
                              ) : (
                                <span className="capitalize text-dark-text-secondary">{u.role}</span>
                              )}
                            </td>
                            {canAdmin && (
                              <td className="py-2 text-center align-middle">
                                {u.user_email.toLowerCase() !== currentUserEmail.toLowerCase() ? (
                                  <button
                                    type="button"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-900/50 text-red-300 hover:bg-red-900/30 text-lg leading-none"
                                    title="Remove access"
                                    onClick={() => handleRemoveUser(u.user_email)}
                                  >
                                    −
                                  </button>
                                ) : (
                                  <span className="text-dark-text-secondary text-xs">—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                        {canAdmin && (
                          <tr className="border-b border-dark-border/50">
                            <td className="py-2 pr-2 align-middle">
                              <input
                                className="w-full bg-dark-bg border border-dark-border rounded-lg px-2 py-1.5 text-sm"
                                placeholder="email@company.com"
                                value={shareEmail}
                                onChange={(e) => setShareEmail(e.target.value)}
                              />
                            </td>
                            <td className="py-2 pr-2 align-middle">
                              <select
                                className="w-full max-w-[11rem] bg-dark-bg border border-dark-border rounded-lg px-2 py-1.5 text-sm capitalize"
                                value={shareRole}
                                onChange={(e) => setShareRole(e.target.value)}
                              >
                                <option value="editor">Editor</option>
                                <option value="viewer">Viewer</option>
                                <option value="owner">Owner</option>
                              </select>
                            </td>
                            <td className="py-2 text-center align-middle">
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-indigo-600 text-indigo-200 hover:bg-indigo-600/30 text-lg leading-none"
                                title="Add collaborator"
                                onClick={handleShareAdd}
                              >
                                +
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {!canAdmin && poolUsers.length === 0 && (
                    <p className="text-dark-text-secondary text-sm mt-2">
                      No shared users listed; you may have access as the pool creator.
                    </p>
                  )}
                </div>
              )}

              <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-cyan-400">Members</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {canManualAdd && (
                      <button
                        type="button"
                        data-testid="pool-add-person-button"
                        onClick={() => setShowAddPersonModal(true)}
                        className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm text-white font-medium"
                      >
                        + Add person to pool
                      </button>
                    )}
                    <ReviewFilterPill
                      value={reviewFilter}
                      onChange={setReviewFilter}
                      totalCount={members.length}
                      needsReviewCount={needsReviewCount}
                      onStartQueue={onStartReviewQueue ? handleStartQueue : undefined}
                    />
                  </div>
                </div>
                {manualAddNotice && (
                  <p
                    className="text-sm text-emerald-300 mb-3"
                    role="status"
                    data-testid="pool-manual-add-notice"
                  >
                    {manualAddNotice}
                    <button
                      type="button"
                      className="ml-2 underline text-emerald-200/80"
                      onClick={() => setManualAddNotice(null)}
                    >
                      Dismiss
                    </button>
                  </p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-dark-text-secondary border-b border-dark-border">
                        <th className="pb-2 pr-2">Name</th>
                        <th className="pb-2 pr-2">Title</th>
                        <th className="pb-2 pr-2">Seniority</th>
                        <th className="pb-2 pr-2">Top skills</th>
                        <th className="pb-2 pr-2">Review</th>
                        <th className="pb-2 pr-2">Source</th>
                        <th className="pb-2 pr-2">Added</th>
                        <th className="pb-2 pr-2">Updated</th>
                        <th className="pb-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {displayedMembers.map((m) => (
                        <PoolMemberRow
                          key={m.id}
                          member={m}
                          isHighlighted={m.person_id === highlightPersonId}
                          onNavigatePerson={onNavigatePerson}
                          canWrite={canWrite}
                          onRemove={handleRemoveMember}
                          onReview={
                            m.person_review_status === 'needs_review' ? handleRowReview : undefined
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                  {displayedMembers.length === 0 && reviewFilter === 'all' && (
                    <p className="text-dark-text-secondary text-sm py-4">No candidates yet. Add a person manually or use Import below.</p>
                  )}
                  {displayedMembers.length === 0 && reviewFilter === 'needs_review' && (
                    <p
                      className="text-dark-text-secondary text-sm py-4"
                      data-testid="review-queue-empty"
                    >
                      ✓ All caught up — no candidates in this pool need review.
                    </p>
                  )}
                </div>
              </div>

              {canWrite && (
                <div className="bg-dark-card border border-dark-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-cyan-400 mb-3">Import</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(['paste', 'cv', 'bulk', 'sheets'] as ImportTab[]).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setImportTab(t)}
                        className={`px-3 py-1 rounded-lg text-sm capitalize ${
                          importTab === t ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-300'
                        }`}
                      >
                        {t === 'cv' ? 'CV upload' : t === 'bulk' ? 'CSV / Excel' : t}
                      </button>
                    ))}
                  </div>

                  {importTab === 'paste' && (
                    <div className="space-y-2">
                      <textarea
                        className="w-full min-h-[120px] bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-sm disabled:opacity-60"
                        placeholder="Paste CV text or profile notes…"
                        value={pasteText}
                        disabled={isImporting}
                        onChange={(e) => setPasteText(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={handleImportPaste}
                        disabled={isImporting || !pasteText.trim()}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-sm text-white"
                      >
                        {isImporting && importState.mode === 'paste' ? 'Parsing…' : 'Parse and add'}
                      </button>
                    </div>
                  )}

                  {importTab === 'cv' && (
                    <div>
                      <label
                        className={`block border border-dashed border-dark-border rounded-xl p-6 text-center ${
                          isImporting
                            ? 'opacity-60 cursor-not-allowed'
                            : 'cursor-pointer hover:border-indigo-500/50'
                        }`}
                      >
                        <span className="text-sm text-dark-text-secondary">
                          {isImporting && importState.mode === 'cv'
                            ? 'Importing — please wait…'
                            : 'Click to upload PDF or DOCX'}
                        </span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={handleCvChange}
                          disabled={isImporting}
                        />
                      </label>
                    </div>
                  )}

                  <ImportProgressPanel
                    state={importState}
                    onCancel={handleImportCancel}
                    onDismiss={handleImportDismiss}
                    onRetry={handleImportRetry}
                    onOpenPerson={onNavigatePerson ? handleImportOpenPerson : undefined}
                  />

                  {importTab === 'bulk' && (
                    <div>
                      <p className="text-xs text-dark-text-secondary mb-2">
                        First row: headers including name (required), title, contact, primary_location, expertise.
                      </p>
                      <label className="block border border-dashed border-dark-border rounded-xl p-6 text-center cursor-pointer hover:border-indigo-500/50">
                        <span className="text-sm text-dark-text-secondary">CSV or Excel file</span>
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          className="hidden"
                          onChange={handleBulkChange}
                        />
                      </label>
                    </div>
                  )}

                  {importTab === 'sheets' && (
                    <div className="space-y-3 text-sm">
                      <p className="text-dark-text-secondary text-xs">
                        Enter the Google Spreadsheet ID (from the URL), optional range (default Sheet1!A:Z). You will
                        consent with the same Google account as Talent First login. Backend must have GOOGLE_CLIENT_SECRET,
                        public API URL (env or Admin → Public URLs), and POOL_SHEETS_TOKEN_ENCRYPTION_KEY set.
                      </p>
                      <input
                        className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2"
                        placeholder="Spreadsheet ID"
                        value={sheetIdInput}
                        onChange={(e) => setSheetIdInput(e.target.value)}
                      />
                      <input
                        className="w-full bg-dark-bg border border-dark-border rounded-lg px-3 py-2"
                        placeholder="Range e.g. Sheet1!A:Z"
                        value={sheetRangeInput}
                        onChange={(e) => setSheetRangeInput(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={handleConnectSheets}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white"
                      >
                        Connect Google Sheet
                      </button>

                      <ul className="mt-4 space-y-2">
                        {sheetLinks.map((l) => (
                          <li
                            key={l.id}
                            className="flex flex-wrap items-center justify-between gap-2 bg-dark-bg rounded-lg px-3 py-2 border border-dark-border"
                          >
                            <div className="text-xs">
                              <div className="font-mono break-all">{l.spreadsheet_id}</div>
                              <div className="text-dark-text-secondary">
                                Last sync: {l.last_sync_at ? new Date(l.last_sync_at).toLocaleString() : 'never'} —{' '}
                                {l.last_sync_status || '—'}
                              </div>
                              {l.last_error && <div className="text-red-400">{l.last_error}</div>}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                className="text-xs px-2 py-1 bg-gray-700 rounded"
                                onClick={() => handleSync(l.id)}
                              >
                                Sync now
                              </button>
                              {canAdmin && (
                                <button
                                  type="button"
                                  className="text-xs px-2 py-1 bg-red-900/40 rounded text-red-200"
                                  onClick={() => handleDeleteLink(l.id)}
                                >
                                  Disconnect
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {showAddPersonModal && selectedId != null && selectedPool && (
                <AddPoolMemberModal
                  poolId={selectedId}
                  poolName={selectedPool.name}
                  onClose={() => setShowAddPersonModal(false)}
                  onSuccess={handleManualAddSuccess}
                />
              )}
            </>
          )}
        </main>
      </div>

    </div>
  );
};

function formatPoolSourceKind(kind: string): string {
  const labels: Record<string, string> = {
    manual: 'Manual',
    cv_upload: 'CV upload',
    paste_text: 'Paste',
    bulk_file: 'Bulk file',
    google_sheets: 'Google Sheets',
  };
  return labels[kind] ?? kind.replace(/_/g, ' ');
}

// =============================================================================
// Phase 3B: enriched members table — row + chips
// =============================================================================

interface PoolMemberRowProps {
  member: PoolMember;
  isHighlighted: boolean;
  onNavigatePerson?: (personId: number) => void;
  canWrite: boolean;
  onRemove: (personId: number) => void;
  /**
   * Phase 3F — when present AND the row is `needs_review`, an inline
   * `Review →` CTA renders next to the remove action and opens the candidate
   * modal in queue mode. Rows that are already reviewed/unreviewed suppress
   * the CTA entirely.
   */
  onReview?: (personId: number) => void;
}

/**
 * One row in the candidate pool members table. Renders the enriched Phase 3B
 * columns (seniority, top skills, review badge, location under name) while
 * staying tolerant of older/legacy rows that don't carry the enrichment.
 */
const PoolMemberRow: React.FC<PoolMemberRowProps> = ({
  member: m,
  isHighlighted,
  onNavigatePerson,
  canWrite,
  onRemove,
  onReview,
}) => {
  const reviewStatus: PersonReviewStatus | null = m.person_review_status ?? null;

  return (
    <tr
      className={`border-b border-dark-border/50 transition-colors ${
        isHighlighted ? 'bg-green-950/40 ring-1 ring-green-500/60' : ''
      }`}
      data-highlighted={isHighlighted || undefined}
      data-testid="pool-member-row"
      data-person-id={m.person_id}
      data-review-status={reviewStatus ?? 'none'}
    >
      <td className="py-2 pr-2 align-top">
        {onNavigatePerson ? (
          <button
            type="button"
            onClick={() => onNavigatePerson(m.person_id)}
            className="text-left text-indigo-300 hover:text-indigo-200 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded"
            data-testid="pool-member-open"
            aria-label={`Open profile for ${m.person_name ?? 'candidate'}`}
          >
            {m.person_name || 'Unknown candidate'}
          </button>
        ) : (
          <span>{m.person_name}</span>
        )}
        {m.person_primary_location && (
          <div
            className="text-xs text-dark-text-secondary mt-0.5"
            data-testid="pool-member-location"
          >
            {m.person_primary_location}
          </div>
        )}
      </td>
      <td className="py-2 pr-2 align-top text-dark-text-secondary">
        {m.person_title || '—'}
      </td>
      <td className="py-2 pr-2 align-top">
        <SeniorityChip value={m.person_seniority_level ?? null} />
      </td>
      <td className="py-2 pr-2 align-top">
        <TopSkillsCell
          skills={m.top_skills ?? []}
          total={m.total_skills_count ?? (m.top_skills?.length ?? 0)}
        />
      </td>
      <td className="py-2 pr-2 align-top">
        <ReviewBadge status={reviewStatus} />
      </td>
      <td className="py-2 pr-2 align-top text-xs">{formatPoolSourceKind(m.source_kind)}</td>
      <td className="py-2 pr-2 align-top text-xs text-dark-text-secondary">
        {new Date(m.added_at).toLocaleString()}
      </td>
      <td className="py-2 pr-2 align-top text-xs text-dark-text-secondary">
        {m.person_updated_at ? new Date(m.person_updated_at).toLocaleString() : '—'}
      </td>
      <td className="py-2 align-top">
        <div className="flex items-center justify-end gap-3 whitespace-nowrap">
          {onReview && reviewStatus === 'needs_review' && (
            <button
              type="button"
              className="text-amber-300 text-xs font-semibold hover:underline focus:outline-none focus:ring-1 focus:ring-amber-400 rounded"
              onClick={() => onReview(m.person_id)}
              data-testid="pool-member-review"
              aria-label={`Review ${m.person_name ?? 'candidate'}`}
            >
              Review →
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className="text-red-400 text-xs hover:underline"
              onClick={() => onRemove(m.person_id)}
            >
              Remove
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

/**
 * Phase 3F — segmented filter pill that sits above the members table.
 * Switching to "Needs review" narrows the rendered list and reveals a
 * "Start queue" CTA when there is at least one candidate to review.
 */
interface ReviewFilterPillProps {
  value: 'all' | 'needs_review';
  onChange: (next: 'all' | 'needs_review') => void;
  totalCount: number;
  needsReviewCount: number;
  onStartQueue?: () => void;
}

const ReviewFilterPill: React.FC<ReviewFilterPillProps> = ({
  value,
  onChange,
  totalCount,
  needsReviewCount,
  onStartQueue,
}) => {
  const base =
    'px-3 py-1 rounded-full text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const active = 'bg-gray-700 text-white';
  const inactive = 'text-dark-text-secondary hover:text-dark-text';
  const disabled =
    needsReviewCount === 0 ? 'opacity-60 cursor-not-allowed hover:text-dark-text-secondary' : '';
  return (
    <div
      className="flex items-center gap-3"
      role="group"
      aria-label="Review filter"
      data-testid="review-filter-pill"
    >
      <div className="flex items-center gap-1 rounded-full bg-dark-bg border border-dark-border p-1">
        <button
          type="button"
          className={`${base} ${value === 'all' ? active : inactive}`}
          aria-pressed={value === 'all'}
          onClick={() => onChange('all')}
          data-testid="review-filter-all"
        >
          All members · {totalCount}
        </button>
        <button
          type="button"
          className={`${base} ${value === 'needs_review' ? active : inactive} ${disabled}`}
          aria-pressed={value === 'needs_review'}
          onClick={() => {
            if (needsReviewCount > 0) onChange('needs_review');
          }}
          disabled={needsReviewCount === 0}
          data-testid="review-filter-needs-review"
        >
          Needs review · {needsReviewCount}
        </button>
      </div>
      {onStartQueue && needsReviewCount > 0 && (
        <button
          type="button"
          className="text-xs font-semibold text-indigo-300 hover:text-indigo-200 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded"
          onClick={onStartQueue}
          data-testid="review-start-queue"
        >
          {needsReviewCount === 1
            ? '1 needs your review · Start queue →'
            : `${needsReviewCount} need your review · Start queue →`}
        </button>
      )}
    </div>
  );
};

const SeniorityChip: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) {
    return <span className="text-dark-text-secondary text-xs">—</span>;
  }
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-900/40 border border-emerald-500/60 text-emerald-100"
      data-testid="seniority-chip"
    >
      {value}
    </span>
  );
};

const TopSkillsCell: React.FC<{
  skills: PoolMemberTopSkill[];
  total: number;
}> = ({ skills, total }) => {
  if (skills.length === 0) {
    return <span className="text-dark-text-secondary text-xs">—</span>;
  }
  const overflow = Math.max(0, total - skills.length);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="top-skills-cell">
      {skills.map((s) => (
        <span
          key={s.skill_id}
          title={`${s.name} · confidence ${s.confidence.toFixed(2)}`}
          className="inline-block px-2 py-0.5 rounded-full text-xs bg-indigo-900/50 border border-indigo-500/40 text-indigo-100"
          data-testid="skill-chip"
        >
          {s.name}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="inline-block px-2 py-0.5 rounded-full text-xs bg-dark-bg border border-dark-border text-dark-text-secondary"
          data-testid="skill-overflow"
          aria-label={`${overflow} more skills`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
};

const ReviewBadge: React.FC<{ status: PersonReviewStatus | null }> = ({ status }) => {
  // `unreviewed` is the default; don't clutter every row with a neutral pill.
  if (!status || status === 'unreviewed') {
    return <span className="text-dark-text-secondary text-xs">—</span>;
  }
  if (status === 'needs_review') {
    return (
      <span
        className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-700/80 text-amber-50"
        data-testid="review-badge"
        data-status="needs_review"
      >
        needs_review
      </span>
    );
  }
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-800/80 text-emerald-50"
      data-testid="review-badge"
      data-status="reviewed"
    >
      reviewed
    </span>
  );
};
